import React, { useState } from "react";
import { FiEye, FiEyeOff, FiHeadphones } from "react-icons/fi";

const validateEmail = (value) => {
  if (!value) {
    return "Please enter a valid email address.";
  }
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!pattern.test(value)) {
    return "Please enter a valid email address.";
  }
  return "";
};

const validatePassword = (value) => {
  if (!value || value.length < 8) {
    return "Password must be at least 8 characters.";
  }
  return "";
};

const InputField = ({
  label,
  name,
  type,
  value,
  onChange,
  placeholder,
  error,
  children,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 text-left">{label}</label>
    <div className="mt-2 relative">
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#19C463] ${
          error ? "border-red-500" : "border-gray-200"
        }`}
      />
      {children}
    </div>
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

const VendorLoginScreen = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(null); // null, true, or false
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const navigate = require("react-router-dom").useNavigate();

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "email") setEmail(value);
    if (name === "password") setPassword(value);
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    if (emailError) {
      nextErrors.email = emailError;
    }
    if (passwordError) {
      nextErrors.password = passwordError;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setMessage("");
    setSuccess(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      setMessage(data.message);
      setSuccess(data.success);
      setTimeout(() => setMessage(""), 2000);
      if (data.success && data.token) {
        localStorage.setItem("vendorToken", data.token);
        navigate("/vendor-home", { state: { loginSuccess: data.message } });
      }
    } catch (err) {
      setMessage("Network error. Please try again.");
      setSuccess(false);
      setTimeout(() => setMessage(""), 2000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F1F3F2]">
      {message && (
        <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg text-sm font-semibold animate-fadeInOut ${success ? 'bg-[#19C463] text-white' : 'bg-red-500 text-white'}`} style={{ minWidth: '220px', textAlign: 'center', letterSpacing: '0.5px' }}>
          {message}
        </div>
      )}
      <div className="max-w-sm w-full">
        <div className="text-center">
          <img src="/BUY_iT_Vendor_Logo-removebg-preview.png" alt="BUY iT Vendor Logo" className="mx-auto h-14 w-14 rounded-xl shadow-md object-cover" />
          <h1 className="mt-4 text-2xl font-extrabold text-gray-900">
            BUY iT Vendor Center
          </h1>
          <p className="mt-2 text-sm font-semibold text-gray-700">
            Selling Made Easy, Faster And Profitable
          </p>
          <p className="mt-3 text-sm text-gray-600">
            Login to manage your store and grow your business.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <InputField
            label="Email"
            name="email"
            type="email"
            value={email}
            onChange={handleChange}
            placeholder="vendor@buyit.com"
            error={errors.email}
          />

          <InputField
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={handleChange}
            placeholder="Enter your password"
            error={errors.password}
          >
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
            </button>
          </InputField>

          <button
            type="submit"
            disabled={loading}
            className={`w-full rounded-lg py-3 text-sm font-semibold transition ${
              loading
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[#19C463] hover:bg-green-600 text-white"
            }`}
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          <span>New to BUY iT? </span>
          <button
            type="button"
            className="text-[#19C463] font-semibold"
            onClick={() => navigate("/vendor-signup")}
          >
            Create vendor account
          </button>
        </div>
      </div>

      <button
        type="button"
        className="fixed bottom-6 right-6 flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-lg"
        onClick={() => navigate("/customer-care")}
      >
        <FiHeadphones className="h-4 w-4 text-gray-700" />
        Support
      </button>
    </div>
  );
};

export default VendorLoginScreen;

